// Google Analytics 4 and Google Ads Configuration
// Set your Google Analytics Measurement ID in .env file as REACT_APP_GA_TRACKING_ID
// Set your Google Ads Conversion ID in .env file as REACT_APP_GOOGLE_ADS_CONVERSION_ID

const GA_TRACKING_ID = process.env.REACT_APP_GA_TRACKING_ID || 'G-B5MHNX0VW3';
const GOOGLE_ADS_CONVERSION_ID = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID || 'AW-XXXXXXXXXX';

// Initialize Google Analytics and Google Ads
export const initGA = () => {
  // Don't initialize if no tracking ID is provided
  if (!GA_TRACKING_ID || GA_TRACKING_ID === 'G-B5MHNX0VW3') {
    console.warn('Google Analytics not initialized: Please set REACT_APP_GA_TRACKING_ID in your .env file');
    return;
  }

  if (typeof window !== 'undefined' && window.gtag) {
    return;
  }

  // Load Google Analytics script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', GA_TRACKING_ID, {
    page_title: document.title,
    page_location: window.location.href,
    // Enable Google Ads features
    allow_google_signals: true,
    allow_ad_personalization_signals: true,
  });

  // Initialize Google Ads if conversion ID is provided
  if (GOOGLE_ADS_CONVERSION_ID && GOOGLE_ADS_CONVERSION_ID !== 'AW-XXXXXXXXXX') {
    window.gtag('config', GOOGLE_ADS_CONVERSION_ID, {
      allow_google_signals: true,
      allow_ad_personalization_signals: true,
    });
  }
};

// Track page views
export const trackPageView = (pageTitle, pagePath) => {
  if (!GA_TRACKING_ID || GA_TRACKING_ID === 'G-XXXXXXXXXX') return;
  
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_TRACKING_ID, {
      page_title: pageTitle,
      page_location: window.location.origin + pagePath,
    });
  }
};

// Track custom events
export const trackEvent = (action, category, label, value) => {
  if (!GA_TRACKING_ID || GA_TRACKING_ID === 'G-XXXXXXXXXX') return;
  
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Track specific anger management events
export const trackAngerManagementEvent = (eventType, details = {}) => {
  if (!GA_TRACKING_ID || GA_TRACKING_ID === 'G-XXXXXXXXXX') return;
  
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventType, {
      event_category: 'Anger Management',
      ...details,
    });
  }
};

// Track breathing exercise usage
export const trackBreathingExercise = (action, duration) => {
  trackAngerManagementEvent('breathing_exercise', {
    event_label: action,
    value: duration,
  });
};

// Track journal entries
export const trackJournalEntry = (action) => {
  trackAngerManagementEvent('journal_entry', {
    event_label: action,
  });
};

// Track technique usage
export const trackTechniqueUsage = (techniqueName) => {
  trackAngerManagementEvent('technique_used', {
    event_label: techniqueName,
  });
};

// Track resource clicks
export const trackResourceClick = (resourceType, resourceName) => {
  trackAngerManagementEvent('resource_click', {
    event_label: resourceName,
    resource_type: resourceType,
  });
};

// Track user engagement
export const trackEngagement = (section, action) => {
  trackAngerManagementEvent('user_engagement', {
    event_label: `${section}_${action}`,
  });
};

// Google Ads Conversion Tracking
export const trackGoogleAdsConversion = (conversionId, conversionLabel, value = null, currency = 'USD') => {
  if (!conversionId || conversionId === 'AW-XXXXXXXXXX') return;
  
  if (typeof window !== 'undefined' && window.gtag) {
    const conversionData = {
      send_to: `${conversionId}/${conversionLabel}`,
      value: value,
      currency: currency,
    };
    
    window.gtag('event', 'conversion', conversionData);
  }
};

// Track specific conversions for anger management
export const trackBreathingExerciseCompletion = (duration) => {
  // Track as a micro-conversion
  trackGoogleAdsConversion(
    GOOGLE_ADS_CONVERSION_ID,
    'breathing_exercise_completion',
    duration,
    'USD'
  );
  
  trackAngerManagementEvent('breathing_exercise_completed', {
    event_label: 'completed',
    value: duration,
  });
};

export const trackJournalEntryCompletion = () => {
  // Track as a micro-conversion
  trackGoogleAdsConversion(
    GOOGLE_ADS_CONVERSION_ID,
    'journal_entry_completion',
    1,
    'USD'
  );
  
  trackAngerManagementEvent('journal_entry_completed', {
    event_label: 'completed',
  });
};

export const trackTechniqueCompletion = (techniqueName) => {
  // Track as a micro-conversion
  trackGoogleAdsConversion(
    GOOGLE_ADS_CONVERSION_ID,
    'technique_completion',
    1,
    'USD'
  );
  
  trackAngerManagementEvent('technique_completed', {
    event_label: techniqueName,
  });
};

export const trackResourceDownload = (resourceName) => {
  // Track as a micro-conversion
  trackGoogleAdsConversion(
    GOOGLE_ADS_CONVERSION_ID,
    'resource_download',
    1,
    'USD'
  );
  
  trackAngerManagementEvent('resource_downloaded', {
    event_label: resourceName,
  });
};

// Enhanced Conversion Tracking
export const setEnhancedConversion = (email = null, phone = null, address = null) => {
  if (typeof window !== 'undefined' && window.gtag) {
    const enhancedData = {};
    
    if (email) {
      enhancedData.email = email;
    }
    
    if (phone) {
      enhancedData.phone_number = phone;
    }
    
    if (address) {
      enhancedData.address = address;
    }
    
    if (Object.keys(enhancedData).length > 0) {
      window.gtag('set', 'user_data', enhancedData);
    }
  }
};

// Dynamic Remarketing
export const setDynamicRemarketing = (pageType, customParameters = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GOOGLE_ADS_CONVERSION_ID, {
      page_type: pageType,
      custom_parameters: customParameters,
    });
  }
};

// Track user journey for remarketing
export const trackUserJourney = (step, details = {}) => {
  trackAngerManagementEvent('user_journey', {
    event_label: step,
    ...details,
  });
  
  // Set dynamic remarketing based on user journey
  setDynamicRemarketing(step, details);
};

const analytics = {
  initGA,
  trackPageView,
  trackEvent,
  trackAngerManagementEvent,
  trackBreathingExercise,
  trackJournalEntry,
  trackTechniqueUsage,
  trackResourceClick,
  trackEngagement,
  // Google Ads functions
  trackGoogleAdsConversion,
  trackBreathingExerciseCompletion,
  trackJournalEntryCompletion,
  trackTechniqueCompletion,
  trackResourceDownload,
  setEnhancedConversion,
  setDynamicRemarketing,
  trackUserJourney,
};

export default analytics; 